import {
  Accordion as MuiAccordion,
  AccordionProps as MuiAccordionProps,
  AccordionSummary as MuiAccordionSummary,
  AccordionSummaryProps as MuiAccordionSummaryProps,
  AccordionDetails as MuiAccordionDetails,
  AccordionDetailsProps,
  styled,
} from '@mui/material';
import { createContext, FC, useContext } from 'react';
import { spacingSizes } from '../../theme';
import { ExpandMore } from '@mui/icons-material';

type SpacingSize = keyof typeof spacingSizes;

type AccordionContextValue = {
  spacing: SpacingSize;
  sizeSpacing: SpacingSize;
};

const AccordionContext = createContext<AccordionContextValue>({
  spacing: 'lg',
  sizeSpacing: 'lg',
});

type AccordionProps = MuiAccordionProps & {
  spacing?: SpacingSize;
  size?: 'sm' | 'md';
};

const sizeToSpacingSize: Record<'sm' | 'md', SpacingSize> = {
  md: 'lg',
  sm: 'sm',
};

const StyledAccordion = styled(MuiAccordion, {
  shouldForwardProp: (prop) => prop !== 'sizeSpacing',
})<{ sizeSpacing: SpacingSize }>(({ theme, sizeSpacing }) => ({
  paddingBottom: theme.spacing(theme.spacingSizes[sizeSpacing]),
}));

const StyledAccordionSummary = styled(MuiAccordionSummary, {
  shouldForwardProp: (prop) => prop !== 'sizeSpacing',
})<{ sizeSpacing: SpacingSize }>(({ theme, sizeSpacing }) => ({
  ...theme.typography.h5,
  paddingTop: theme.spacing(theme.spacingSizes[sizeSpacing]),
  '& .MuiAccordionSummary-expandIconWrapper': {
    marginLeft: theme.spacing(theme.spacingSizes.sm),
  },
}));

const StyledAccordionDetails = styled(MuiAccordionDetails, {
  shouldForwardProp: (prop) => prop !== 'spacing',
})<{ spacing: SpacingSize }>(({ theme, spacing }) => ({
  paddingTop: theme.spacing(theme.spacingSizes[spacing]),
}));

export const Accordion: FC<AccordionProps> = ({
  spacing = 'lg',
  size = 'md',
  ...rest
}) => {
  const sizeSpacing = sizeToSpacingSize[size];
  return (
    <AccordionContext.Provider value={{ spacing, sizeSpacing }}>
      <StyledAccordion sizeSpacing={sizeSpacing} {...rest} />
    </AccordionContext.Provider>
  );
};

export const AccordionSummary: FC<MuiAccordionSummaryProps> = ({
  expandIcon,
  ...props
}) => {
  const { sizeSpacing } = useContext(AccordionContext);
  return (
    <StyledAccordionSummary
      expandIcon={expandIcon ?? <ExpandMore />}
      sizeSpacing={sizeSpacing}
      {...props}
    />
  );
};

export const AccordionDetails: FC<AccordionDetailsProps> = (props) => {
  const { spacing } = useContext(AccordionContext);
  return <StyledAccordionDetails spacing={spacing} {...props} />;
};
